/**
 * Codex PR #784 round-9 P1 (`lod/lodPolicy.ts:225`) — VIEWPORT-SCALE LIFT
 * REQUESTS.
 *
 * # The gap this closes
 *
 * Round 8 removed the blanket `Block` exemption from `isAlwaysLiftedStubType`
 * (`lod/lodPolicy.ts`) and made a dropped stub recoverable on demand. The
 * recovery is driven by ONE camera: `scene/FrameLoops.tsx` re-classifies the
 * spatial index against the MAIN camera's rect + zoom and lifts whatever became
 * big enough (`entityResolverRef` → publish + `emitReliftDirectMeshes` →
 * `BatchedLayer.addEntity`).
 *
 * A paper-space VIEWPORT is a second, independent window onto the same model
 * with its OWN scale (`viewHeight` model units mapped onto `height` paper
 * units) and its own frozen-layer set. An INSERT that is sub-pixel under the
 * model-wide fit — so the bulk-load pre-classify drops it — can be metres wide
 * inside such a viewport, and the reverse is equally true. Neither
 * `ViewportCullingAdapter.query` nor `rendering/viewportRenderPass.ts
 * renderViewportPass` lifts anything: the first computes ids, the second masks
 * objects that ALREADY exist in the scene. So the viewport marked the INSERT
 * visible while no bucket slot and no direct mesh existed for it, and the block
 * stayed blank no matter how large that viewport displayed it.
 *
 * # The shape of the fix
 *
 * This module does NOT lift and does NOT draw. It answers only "what does each
 * viewport need, measured at THAT viewport's scale" and publishes the union
 * into a {@link ViewportLiftRequests} record: the ids to MATERIALISE (each with
 * the largest requesting viewport's scale, which is the scale their meshes must
 * be built for — round-11 P1) and the ids that only need a POINT PROXY
 * (round-11 P2). `scene/FrameLoops.tsx` unions the first into the very same
 * newly-visible diff its main-camera pass already feeds to `entityResolverRef`
 * — so there is exactly ONE lift path, one publish, one
 * `emitReliftDirectMeshes`, and the per-viewport path cannot drift from the
 * main-camera path the way the two LOD predicates once did — and feeds the
 * second into a points cloud, which is the only thing a point-band id has ever
 * been entitled to.
 *
 * Codex PR #784 round-12 P2 — the point-proxy answer carries WHICH viewports
 * asked ({@link ViewportLiftRequests.points}). A dot is geometry: painting the
 * union of every viewport's ids in every viewport draws, inside viewport B,
 * references B classifies `"skip"` or freezes. The membership travels to the
 * cloud, which stamps it for the VP mask
 * (`rendering/viewportPointProxyStamp.ts`).
 *
 * Publishing the FULL per-viewport set (not just the not-yet-lifted part) is
 * deliberate: `FrameLoops` Pass 1 REMOVES every lifted id that is absent from
 * the frame's visible set, and the main paper camera's rect says nothing about
 * what a viewport window shows. Handing it the whole set is what keeps a
 * viewport's geometry lifted while the layout camera looks elsewhere; the
 * already-lifted ids cost one `Set.has` each in Pass 2 and allocate nothing.
 *
 * # Per-frame cost
 *
 * The viewport pass runs on EVERY frame, so the recompute is signature-gated
 * ({@link viewportLiftSignature}): unchanged viewports at an unchanged camera
 * scale re-publish nothing, bump no version, and allocate nothing, which leaves
 * `FrameLoops` on its existing camera-delta early-out. When the signature does
 * move, the walk is O(ids in view) with one `getBounds` (an O(1) rbush bbox
 * read) per id — the same order as `computeVisibleLayers`, which the viewport
 * orchestrator already runs per viewport per frame.
 */

import type { ViewportEntity } from "@dxf-ai-editor/drawing-model";

import {
  boundsScreenSizePx,
  isDegenerateBounds,
  lodBucketForBounds,
  lodBucketIsLifted,
} from "../lod";
import { VIEWPORT_ENTITY_BUDGET } from "../rendering/viewportRenderPass";
import type { SpatialIndex } from "../types";

/**
 * Ids every active paper-space viewport needs materialised, plus a revision
 * counter. Owned by `Scene3DStage` (it outlives the viewport orchestrator,
 * which mounts only on a layout) and read by `scene/FrameLoops.tsx`.
 *
 * `version` is the ONLY change signal `FrameLoops` watches — it re-runs its
 * visible-set pass when the number moves even if the camera did not, and stores
 * the value it processed. `lift` / `points` are SWAPPED, never mutated in
 * place, so a consumer that captured the previous collection still sees a
 * consistent snapshot.
 */
export interface ViewportLiftRequests {
  /** Bumped whenever `lift` / `points` are replaced with DIFFERENT contents. */
  version: number;
  /**
   * Union across viewports of the ids each one renders at full detail, mapped
   * to the LARGEST model scale (CSS px per model unit) at which a requesting
   * viewport will render that id.
   *
   * Codex PR #784 round-11 P1 (`scene/SceneBulkLoad.ts:1401`) — the scale is
   * carried WITH the request, not recomputed downstream, because it is the
   * scale the lift must BUILD against. `scene/FrameLoops.tsx` hands it to the
   * on-demand resolver as `EntityLiftRequest.emitScale`, and
   * `SceneBulkLoad.ts` emits the id's direct meshes at
   * `max(liveCameraZoom(), emitScale)`: a glyph the main paper camera puts
   * below the PR-V5 2 px gate but a magnifying viewport renders legibly must
   * be built for the camera that will actually draw it. The MAXIMUM is the
   * right reduction over several requesting viewports — a mesh built for the
   * largest scale is still correct at every smaller one, and the reverse is
   * not true.
   */
  lift: ReadonlyMap<string, number>;
  /**
   * Codex PR #784 round-11 P2 (`viewports/viewportLiftRequests.ts:219`) — ids
   * a viewport renders in the POINT band (0.5-2 px at its own scale) and that
   * are therefore deliberately absent from {@link lift}.
   *
   * These are NOT a lift request: `scene/FrameLoops.tsx` feeds them to a points
   * cloud as one dot each (resolved through the NON-EXPANDING proxy —
   * `EntityLiftRequest.proxyOnly` — so no wrapper, satellite tree or direct
   * mesh is ever materialised for them). Without this set an id below 0.5 px
   * under the main layout camera but inside the point band in a viewport had
   * neither lifted geometry NOR a dot, because the only points cloud is built
   * from the MAIN camera's `groups.point`.
   *
   * Codex PR #784 round-12 P2 (`scene/FrameLoops.tsx:1097`) — the value is the
   * MEMBERSHIP: the ids of the viewports that asked for that dot, in
   * `viewports` order. A dot is geometry, and geometry that no viewport asked
   * for must not paint in that viewport's window — so the attribution has to
   * survive all the way to the cloud, which stamps it
   * (`rendering/viewportPointProxyStamp.ts`) for
   * `rendering/viewportRenderPass.ts applySceneVisibilityMask`.
   *
   * It stays ONE entry per id — never one per (viewport, id) pair — so the feed
   * is still non-expanding: K requesting viewports cost K strings in one array,
   * not K dots.
   */
  points: ReadonlyMap<string, readonly string[]>;
  /**
   * Largest value in {@link lift}, or 0 when it is empty. `FrameLoops` uses it
   * as the O(1) gate on the pending-text retry sweep: no per-id lookup can
   * exceed it, so a frame whose scales cannot have unblocked anything skips
   * the sweep without touching the pending set.
   */
  maxLiftScale: number;
}

/**
 * What one pass of {@link collectViewportLiftIds} decided: the ids to lift
 * (with the scale each must be built for) and the ids that get a point proxy
 * instead. Mutable — it is built once per recompute and handed straight to
 * {@link publishViewportLiftRequests}, which either adopts or discards it.
 */
export interface ViewportLiftPlan {
  /** id → the largest requesting viewport's model scale. */
  lift: Map<string, number>;
  /**
   * Point-band id → the ids of the viewports that render it in that band
   * (round-12 P2). Disjoint from {@link lift}; every value is non-empty.
   */
  points: Map<string, string[]>;
}

/** A fresh, empty request record (model space / no viewports). */
export function createViewportLiftRequests(): ViewportLiftRequests {
  return {
    version: 0,
    lift: new Map<string, number>(),
    points: new Map<string, readonly string[]>(),
    maxLiftScale: 0,
  };
}

/**
 * CSS pixels per MODEL unit for geometry seen through `viewport`.
 *
 * The viewport window is `viewHeight` model units tall and is drawn into a
 * paper-space rectangle `viewport.height` paper units tall; the main
 * orthographic camera turns 1 paper unit into `mainCameraZoom` CSS pixels
 * (`rendering/viewportRenderPass.ts` scissors that exact rect and renders the
 * model through a frustum of height `viewHeight`). So the model → screen scale
 * is `mainCameraZoom × height / viewHeight`.
 *
 * Returns 0 for a degenerate viewport (non-positive `viewHeight` / `height` /
 * zoom), which classifies everything in it as `"skip"` rather than producing
 * Infinity or NaN.
 */
export function viewportModelScalePx(
  viewport: ViewportEntity,
  mainCameraZoom: number,
): number {
  const { viewHeight, height } = viewport;
  if (!(viewHeight > 0) || !(height > 0) || !(mainCameraZoom > 0)) return 0;
  const scale = (mainCameraZoom * height) / viewHeight;
  return Number.isFinite(scale) && scale > 0 ? scale : 0;
}

/**
 * Change signature for the request set: the viewport parameters that move the
 * model window or its scale, plus the camera scale and the identity of the
 * geometry the ids come from. Recomputing only when this string moves is what
 * keeps the per-frame cost at one small string build.
 *
 * `mainCameraZoom` is quantised into ~1% steps: a continuous wheel-zoom would
 * otherwise re-publish (and re-wake `FrameLoops`) on every frame, and a 1%
 * scale change cannot move an entity across the 0.5 px boundary by more than
 * the hysteresis band already absorbs.
 *
 * # The window anchor is BOTH halves — `viewTarget` and `viewCenter`
 *
 * Codex PR #794 round-8 P2 (`viewports/viewportCullingAdapter.ts:161`) — the
 * viewport window is centred on `viewTarget + R(twist)·viewCenter`
 * (`drawing-model`'s `viewportWcsViewCenter`; groups 12/22 are the centre in
 * DCS, whose origin is the WCS target 17/27/37). `ViewportCullingAdapter`'s
 * own cache entry keys on BOTH halves, so a target-only edit correctly makes
 * it recompute and hand back the new visible set. This OUTER signature hashed
 * only `viewCenter`, so the same edit left the id set moving underneath a
 * request plan that was never rebuilt: `Scene3DStage` kept the stale
 * `viewportLiftRequestsRef`, `FrameLoops` never materialised the newly visible
 * ids that sit below the MAIN camera's LOD threshold, and the moved viewport
 * stayed blank or partial until some unrelated field moved. The two layers
 * must read the same anchor.
 *
 * An ABSENT target hashes as `0` — the same additive identity the adapter's
 * cache entry uses — so a viewport that never carried 17/27 produces exactly
 * the string it would with `viewTarget = (0, 0)`, and no existing plan is
 * invalidated by a field the document does not have. The Z component is NOT
 * hashed: `computeViewportModelBounds` builds a 2-D model-space rect and reads
 * only x/y, so folding z in would re-publish for a change that cannot move a
 * single id.
 */
export function viewportLiftSignature(args: {
  viewports: ReadonlyArray<ViewportEntity>;
  mainCameraZoom: number;
  geometryVersion: number;
  /**
   * Revision of the render-satellite map (`SceneBulkLoad.ts` bumps it at the
   * TOP of every pass and again when it repopulates, on both the synchronous
   * and the async chunked lift). Folding it in re-publishes the request set
   * across a bulk load: during the async window `entityResolverRef` is null and
   * `FrameLoops` can lift nothing, so a version consumed in that window would
   * otherwise leave the viewport blank until something else moved.
   */
  bulkLoadVersion: number;
}): string {
  const zoomStep =
    args.mainCameraZoom > 0
      ? Math.round(Math.log(args.mainCameraZoom) / Math.log(1.01))
      : Number.NEGATIVE_INFINITY;
  const parts: string[] = [
    `z${zoomStep}`,
    `g${args.geometryVersion}`,
    `b${args.bulkLoadVersion}`,
  ];
  for (const vp of args.viewports) {
    parts.push(
      `${vp.id}:${vp.viewCenter.x},${vp.viewCenter.y}:` +
        // The other half of the window anchor (see this function's doc).
        // `?? 0` is the additive identity `ViewportCullingAdapter`'s cache
        // entry uses for the same field, so absent hashes as (0, 0).
        `${vp.viewTarget?.x ?? 0},${vp.viewTarget?.y ?? 0}:` +
        `${vp.viewHeight}:` +
        `${vp.viewTwistAngle}:${vp.width}x${vp.height}:` +
        // NUL separator, matching `ViewportCullingAdapter`'s own frozen-layer
        // cache key — a layer name may legitimately contain a comma.
        vp.frozenLayers.slice().sort().join("\u0000"),
    );
  }
  return parts.join("|");
}

/**
 * Collect what every viewport needs, measured at each viewport's OWN model
 * scale: the ids it renders at full detail (with the scale to build them at)
 * and the ids it renders as a single dot.
 *
 * # The rule is the MAIN-CAMERA rule, evaluated at the viewport's scale
 *
 * Codex PR #784 round-10 P1 — "materialised" means the LOD buckets
 * `scene/FrameLoops.tsx` actually lifts: `groups.full ∪ groups.bboxLine`
 * ({@link lodBucketIsLifted}). The first cut of this pass asked a looser
 * question — "is it above the `skip` boundary" — which also claimed the
 * `"point"` band (0.5-2 px). Those ids are the ones the main camera
 * DELIBERATELY keeps out of the lifted set: they render as one dot each in the
 * shared points cloud, precisely so a zoomed-out frame does not resolve them.
 * Since the requested ids are merged straight into `nextVisibleSet`, every
 * one-pixel reference a viewport showed was resolved into its full wrapper +
 * satellite tree — a viewport holding 20 000 one-pixel references to a
 * 50-child definition materialised ~1 000 000 children and stalled the tab,
 * re-creating through the viewport path the exact blow-up round 8 removed.
 *
 * A viewport lifts what it renders at full detail and nothing else.
 *
 * # The point band keeps a proxy — a REAL one (round-11 P2)
 *
 * Codex PR #784 round-11 P2 (`viewports/viewportLiftRequests.ts:219`) — round
 * 10 documented the excluded ids as "keeping their point proxy", which is only
 * true while the MAIN camera also puts them in the point band: `FrameLoops`
 * builds the one points cloud from the main camera's `groups.point`, so an id
 * below 0.5 px there and in the 0.5-2 px band inside a viewport had neither
 * lifted geometry nor a dot and vanished from the viewport entirely. They are
 * therefore collected into {@link ViewportLiftPlan.points}, which `FrameLoops`
 * feeds into that same cloud through the NON-EXPANDING proxy resolve
 * (`EntityLiftRequest.proxyOnly`): one bbox-centre dot in the entity's own
 * resolved colour, no wrapper, no satellites, no direct meshes — exactly the
 * trade the main camera makes for its own point band, and nothing the round-10
 * finding objected to.
 *
 * The two sets are disjoint: an id any viewport lifts needs no dot, so it is
 * removed from `points` at the end of the walk.
 *
 * # Per-id scale
 *
 * `lift` carries the LARGEST requesting viewport's scale (round-11 P1) — see
 * {@link ViewportLiftRequests.lift}. A viewport whose scale cannot beat the
 * one already recorded for an id is skipped for that id, so an id shown by K
 * viewports still costs at most K O(1) bbox reads and never re-classifies
 * needlessly.
 *
 * A DEGENERATE (zero-extent) bbox is measured by the renderer's marker box
 * (`lod/lodPolicy.ts LOD_DEGENERATE_WORLD_DIAGONAL`) but is NOT treated as
 * drawable here: this pass has ids and bboxes, not resolved entities, so it
 * cannot know whether such a reference lifts to a POINT marker or to `null`
 * (`entityDrawsDegenerateMarker` is the entity-shaped test the main-camera
 * classifier uses). Passing `false` keeps the conservative answer — the
 * reference is requested once its marker box reaches the lift band, exactly as
 * the main camera treats the same stub, and it is claimed as a dot only when
 * the same classifier would draw one.
 *
 * `queryVisibleIds` is `ViewportCullingAdapter.query` (already model-space- and
 * frozen-layer-filtered, and cached on the viewport's parameters) — passed in
 * rather than re-derived so the two paths can never disagree about which ids a
 * viewport shows.
 *
 * A viewport whose visible set exceeds {@link VIEWPORT_ENTITY_BUDGET} requests
 * nothing: `renderViewportPass` returns `"budget-lod"` for it without touching
 * the GPU, so lifting its contents — or dotting them — would be pure cost with
 * no pixels.
 */
export function collectViewportLiftIds(args: {
  viewports: ReadonlyArray<ViewportEntity>;
  queryVisibleIds: (viewport: ViewportEntity) => ReadonlySet<string>;
  getBounds: SpatialIndex["getBounds"];
  mainCameraZoom: number;
}): ViewportLiftPlan {
  const lift = new Map<string, number>();
  const points = new Map<string, string[]>();
  for (const viewport of args.viewports) {
    const scale = viewportModelScalePx(viewport, args.mainCameraZoom);
    if (scale <= 0) continue;
    const visibleIds = args.queryVisibleIds(viewport);
    if (visibleIds.size > VIEWPORT_ENTITY_BUDGET) continue;
    for (const id of visibleIds) {
      // Already recorded at a scale this viewport cannot beat: the bucket is
      // monotone in scale, so neither the lift decision nor the max can move.
      const recorded = lift.get(id);
      if (recorded !== undefined && scale <= recorded) continue;
      const bounds = args.getBounds(id);
      if (!bounds) continue;
      const bucket = lodBucketForBounds({
        pixels: boundsScreenSizePx(bounds, scale),
        degenerate: isDegenerateBounds(bounds),
        drawsDegenerateMarker: false,
      });
      if (lodBucketIsLifted(bucket)) lift.set(id, scale);
      else if (bucket === "point") {
        // Codex PR #784 round-12 P2 — record WHICH viewport wants the dot. One
        // entry per id, appended to: an id shown in the point band by K
        // viewports costs K strings, never K dots.
        const owners = points.get(id);
        if (owners) owners.push(viewport.id);
        else points.set(id, [viewport.id]);
      }
    }
  }
  // Disjoint by construction of the final answer: a viewport that lifts an id
  // covers every smaller viewport's dot for it.
  for (const id of lift.keys()) points.delete(id);
  return { lift, points };
}

/** True when the two maps hold the same ids AND the same scale for each. */
export function sameLiftMap(
  a: ReadonlyMap<string, number>,
  b: ReadonlyMap<string, number>,
): boolean {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const [id, scale] of a) {
    // A recorded scale of 0 is impossible (`viewportModelScalePx` rejects it),
    // so `undefined` from a missing id can never collide with a real value.
    if (b.get(id) !== scale) return false;
  }
  return true;
}

/**
 * True when the two point-proxy maps hold the same ids AND the same requesting
 * viewports, in the same order, for each (Codex PR #784 round-12 P2).
 *
 * The order is the `viewports` array's, which is stable frame to frame, so an
 * order-sensitive compare cannot report "unchanged" for a real membership
 * change; a pure REORDER of the viewport list re-publishes one extra time,
 * which costs a single cloud rebuild and never a wrong attribution.
 */
export function samePointMembership(
  a: ReadonlyMap<string, readonly string[]>,
  b: ReadonlyMap<string, readonly string[]>,
): boolean {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const [id, owners] of a) {
    const other = b.get(id);
    if (other === undefined || other.length !== owners.length) return false;
    for (let i = 0; i < owners.length; i++) {
      if (other[i] !== owners[i]) return false;
    }
  }
  return true;
}

/**
 * Normalise the `next` argument of {@link publishViewportLiftRequests}. A bare
 * id Set is the CLEAR form the orchestrator uses when there is no adapter /
 * spatial index and on unmount; treating its ids as lift requests with no
 * viewport scale (0 — the resolver then falls back to the live camera zoom) is
 * the exact pre-round-11 behaviour for the non-empty case.
 */
function toLiftPlan(next: ViewportLiftPlan | ReadonlySet<string>): ViewportLiftPlan {
  if (next instanceof Set) {
    const lift = new Map<string, number>();
    for (const id of next) lift.set(id, 0);
    return { lift, points: new Map<string, string[]>() };
  }
  return next as ViewportLiftPlan;
}

/**
 * Recompute + publish the request set into `requests`, bumping `version` only
 * when the plan actually changed — its lift ids, ANY of their scales, or its
 * point-proxy ids. Returns `true` when a new version was published (the caller
 * then invalidates so `FrameLoops` gets a frame to act on it).
 *
 * A changed SCALE at an unchanged id set is a real change (round-11 P1): it is
 * what re-emits a glyph the previous, smaller scale gated out, so it must bump
 * the version rather than being absorbed as "same ids".
 *
 * The caller gates the (re)computation of `next` on {@link viewportLiftSignature}
 * so a static layout never even builds a candidate plan; this equality test is
 * the second guard, for the case where a signature moved but the resulting plan
 * did not (a pan that keeps the same entities in view).
 */
export function publishViewportLiftRequests(args: {
  requests: ViewportLiftRequests;
  next: ViewportLiftPlan | ReadonlySet<string>;
}): boolean {
  const plan = toLiftPlan(args.next);
  if (
    sameLiftMap(plan.lift, args.requests.lift) &&
    samePointMembership(plan.points, args.requests.points)
  ) {
    return false;
  }
  let maxLiftScale = 0;
  for (const scale of plan.lift.values()) {
    if (scale > maxLiftScale) maxLiftScale = scale;
  }
  args.requests.lift = plan.lift;
  args.requests.points = plan.points;
  args.requests.maxLiftScale = maxLiftScale;
  args.requests.version += 1;
  return true;
}
